// First, before anything reads storage or starts Firebase: presenter mode's presented window.
import { isPresentedWindow, PRESENT_PATH } from './lib/presenter/presentedWindow';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from '../App';
import '../styles/globals.css';
import { ToasterProvider } from './components/ui/Toaster';
import PublicProfileSyncNotices from './components/PublicProfileSyncNotices';
import AppErrorBoundary from './components/AppErrorBoundary';
import { CHUNK_RELOAD_KEY } from './lib/lazyWithRetry';
import { initErrorMonitoring, reportError } from './lib/monitoring/sentry';
import { scrubStoredDrafts } from './lib/draftSanitizer';
import { captureSignupSourceFromUrl } from './lib/signups/signupSourceCapture';

// As early as possible, so errors during Firebase init or first render are still captured.
initErrorMonitoring();

// Older releases drafted signup passwords into browser storage; remove them on every visit.
scrubStoredDrafts();

// Remember a ?src= sign-up source tag from the landing URL for the rest of this tab (it has to
// survive Google sign-in, which drops the query string). Saved on the profile when it is created.
captureSignupSourceFromUrl();

// Successful boot — clear one-time chunk reload flag from prior stale-cache recovery
sessionStorage.removeItem(CHUNK_RELOAD_KEY);

// Try to enable Firestore offline persistence (may fail if Firebase config missing). Not in a
// presenter-mode presented window: it is signed in as someone else and must not share the cache.
(async () => {
  if (isPresentedWindow) return;
  try {
    const { enableOffline } = await import('./lib/firebase-persistence');
    enableOffline();
  } catch (err) {
    console.warn('[main.tsx] Could not enable offline persistence:', err);
  }
})();

const rootElement = document.getElementById('root')!;

// A presented window: the remote control (its own chunk, never downloaded by anyone else). On the
// /present hand-off page it signs in and then loads the persona's screen; the app is not rendered there.
const isPresentHandoff = isPresentedWindow && window.location.pathname === PRESENT_PATH;
if (isPresentedWindow) {
  import('./presenter/presentedController').then((m) => m.startPresentedWindow()).catch((err) => console.warn('[main.tsx] presented window', err));
}

// scripts/prerender-home.mjs bakes the real rendered landing page into dist/index.html's
// #root — but Firebase Hosting's SPA rewrite (`"source": "**", "destination": "/index.html"`)
// serves that SAME file for every route, so `#root` has real matching content only when the
// browser's very first load is genuinely the bare "/" URL (no query string — MainLandingPage
// branches its render on e.g. ?accountDeleted=1, which the prerender capture never has).
// Any other route, or "/" with extra query params, gets stale landing-page markup that does
// NOT match that route's real component tree — hydrating against it would be a guaranteed
// mismatch, so those fall back to the existing non-hydrating createRoot() render.
const isPrerenderedRootLoad =
  window.location.pathname === '/' &&
  window.location.search === '' &&
  rootElement.hasChildNodes();

const appTree = (
  <React.StrictMode>
    <AppErrorBoundary>
      <ToasterProvider>
        {/* Renders nothing; shows the "public profile did not update" toast from any save. */}
        <PublicProfileSyncNotices />
        <App />
      </ToasterProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);

if (isPresentHandoff) {
  rootElement.replaceChildren();
} else if (isPrerenderedRootLoad) {
  ReactDOM.hydrateRoot(rootElement, appTree, {
    // A hydration mismatch degrades to a client re-render (React repairs the DOM), not a
    // crash — but it's exactly the failure mode this change needs real production visibility
    // into, since it wouldn't otherwise surface anywhere manual testing didn't look.
    onRecoverableError: (error, errorInfo) => {
      reportError(error, { source: 'hydrateRoot', componentStack: errorInfo?.componentStack });
    },
  });
} else {
  ReactDOM.createRoot(rootElement).render(appTree);
}